import React, { useEffect, useState, createContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';

import toast from 'components/partials/toast/toast';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Heading from 'components/partials/heading/heading';
import ActionMenu from 'components/partials/action-menu/action-menu';
import FileForm from 'containers/admin/clients/client/tabs-components/files-tab/file/forms/file-form';
import FlashNotification from 'components/partials/flash-notification/flash-notification';

import { useAdminClientFilesOperations } from 'hooks/use-admin-client-files-operations';

import type { AdminUploadClientFileArgs } from 'hooks/client-asset-api';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import { useClient } from 'hooks/api/use-clients';

import { getStatusLabel } from 'components/partials/table-component/helpers/get-status-label';
import { getBreadcrumbsForFile } from 'containers/admin/clients/client/tabs-components/files-tab/file/file.helpers';

import {
  FILE_DETAILS_FORM_VALIDATION_SCHEMA,
  FILES_STATUSES,
  ALERTS,
} from 'containers/admin/clients/client/tabs-components/files-tab/file/file.constants';

import type { AssetDto } from 'api/generated/schemas/assetDto';

import type { CurrentUserHolder } from 'api/market-view-api';

import type {
  Control,
  FieldErrors,
  Resolver,
  UseFormReset,
} from 'react-hook-form';

export type FileData = Partial<AssetDto>;

interface FileContextValue {
  currentUserId?: string | number;
  clientId?: string | number;
  fileId?: string | number;
  control?: Control<Record<string, unknown>>;
  errors?: FieldErrors<Record<string, unknown>>;
  clearErrors?: (name?: string) => void;
  reset?: UseFormReset<Record<string, unknown>>;
  setLoading?: (loading: boolean) => void;
  currentUser?: CurrentUserHolder | string | number;
}

export const FileContext = createContext<FileContextValue>({
  currentUserId: 0,
  clientId: 0,
  fileId: 0,
  control: undefined,
  errors: {},
  clearErrors: () => {},
  reset: () => {},
  setLoading: () => {},
});

interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

interface FileProps {
  pageName?: string;
  fileData?: FileData | null | false;
  isFormDisabled?: boolean;
  setIsFormDisabled?: (disabled: boolean) => void;
  actionMenuItems?: ActionMenuItem[];
  formLoading?: boolean;
  defaultValues?: Record<string, unknown>;
  onSubmit?: (data: Record<string, unknown>) => void;
}

const File = ({
  pageName = 'New file',
  fileData,
  isFormDisabled,
  setIsFormDisabled,
  actionMenuItems = [],
  formLoading,
  defaultValues,
  onSubmit,
}: FileProps): React.JSX.Element => {
  const [, setLoading] = useState<boolean>(false);

  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const params = useParams();

  const { uploadAdminClientFile } = useAdminClientFilesOperations();

  const clientId = params.clientId;

  const { data: currentClient } = useClient(clientId);

  const isNewFile = pageName === 'New file';

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<Record<string, unknown>>({
    mode: 'onChange',
    defaultValues: defaultValues || {
      fileName: null,
      description: '',
      file: {},
    },
    resolver: FILE_DETAILS_FORM_VALIDATION_SCHEMA as unknown as Resolver<
      Record<string, unknown>
    >,
  });

  const onCancel = (): void => {
    if (defaultValues) {
      reset(defaultValues);
    } else {
      navigate(`/admin/clients/${clientId}/files`);
    }

    if (setIsFormDisabled) {
      setIsFormDisabled(true);
    }
  };

  const onFileUpload = (data: Record<string, unknown>): void => {
    if (!clientId || !currentUser.data?.oktaUserId) return;

    setLoading(true);

    uploadAdminClientFile({
      oktaUserId: currentUser.data.oktaUserId,
      clientId,
      ...(data as Pick<AdminUploadClientFileArgs, 'fileName' | 'file' | 'description'>),
    })
      .then((uploadedData) => {
        toast.success({ title: ALERTS.SUCCESS.heading });
        navigate(`/admin/clients/${clientId}/files/${uploadedData.id}`);
      })
      .catch((e: { status?: number }) =>
        toast.error({
          title: 'Error during uploading file',
          message: `We couldn't upload this file because of ${e.status} error. Please try again or contact support.`,
        }),
      )
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (setIsFormDisabled) {
      setIsFormDisabled(!!fileData);
    }
  }, []);

  const fileDataObj = (fileData || null) as FileData | null;

  return (
    <FileContext.Provider
      value={{
        control,
        errors,
        clearErrors,
        reset,
        currentUser: currentUser as unknown as CurrentUserHolder,
        clientId,
        setLoading,
        fileId: fileDataObj?.id,
      }}
    >
      <div className={classNames('w-full', { 'px-8': isNewFile })}>
        <Breadcrumbs
          breadcrumbs={getBreadcrumbsForFile(currentClient, fileDataObj)}
        />
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center">
            <Heading variant="h1">{pageName}</Heading>
            {!!fileDataObj && (
              <div className="ml-6" data-testid="file-status-label">
                {getStatusLabel(fileDataObj.status ?? '', FILES_STATUSES)}
              </div>
            )}
          </div>
          {!!actionMenuItems.length &&
            !(currentUser.data as { fdAnalyst?: boolean } | null)?.fdAnalyst && (
              <ActionMenu
                buttonName="Edit file details"
                options={actionMenuItems}
                onEditClick={() => setIsFormDisabled && setIsFormDisabled(false)}
                isActionMenuDisabled={!isFormDisabled}
              />
            )}
        </div>
        <FileForm
          handleSubmit={handleSubmit}
          onSubmit={onSubmit || onFileUpload}
          onCancel={onCancel}
          isFormDisabled={isFormDisabled}
          loading={formLoading}
        />
        <FlashNotification customClass="top-4" />
      </div>
    </FileContext.Provider>
  );
};

export default File;
